import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Seo from "@/components/site/Seo";

export default function Terms() {
  return (
    <>
      <Seo
        title="Terms & Conditions | ProCraft Solutions"
        description="Terms and conditions governing services, communications, and SMS text messaging with ProCraft Solutions."
      />
      <div className="bg-paper min-h-screen" data-testid="terms-page">
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-16 md:py-24">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#16a34a] hover:text-[#15803d] transition mb-8"
            data-testid="terms-back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>

          <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#16a34a]">
            Legal
          </div>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl text-[#1c1917] mt-2 tracking-tight">
            Terms &amp; Conditions
          </h1>
          <p className="mt-3 text-sm text-[#57534e] font-medium">
            Effective Date: September 22, 2026
          </p>

          <div className="mt-10 space-y-8 text-[#1c1917] leading-relaxed text-[15px]">
            <p>
              These Terms &amp; Conditions govern communications and services
              provided by ProCraft Solutions. By requesting services, scheduling
              work, or communicating with ProCraft Solutions, you agree to these
              terms where applicable.
            </p>

            <section>
              <h2 className="font-display font-bold text-2xl mb-3 text-[#1c1917]">
                Services
              </h2>
              <p className="mb-3">
                ProCraft Solutions provides property improvement and maintenance
                services, including landscaping, lawn maintenance, drainage,
                landscape lighting, outdoor improvement, handyman, and related
                services.
              </p>
              <p>
                Specific work, pricing, materials, schedules, and other project
                details may be provided through estimates, quotes, invoices, or
                other agreements between ProCraft Solutions and the customer.
              </p>
            </section>

            <section>
              <h2 className="font-display font-bold text-2xl mb-3 text-[#1c1917]">
                Estimates and Pricing
              </h2>
              <p className="mb-3">
                Estimates are based on the information and conditions available
                at the time they are prepared. Pricing may change if the scope
                of work changes, unforeseen conditions are discovered, or
                additional work or materials are requested.
              </p>
              <p>
                Any additional work may require customer approval and may result
                in additional charges.
              </p>
            </section>

            <section>
              <h2 className="font-display font-bold text-2xl mb-3 text-[#1c1917]">
                Scheduling
              </h2>
              <p className="mb-3">
                Service dates and arrival times may be affected by weather,
                material availability, property conditions, equipment issues,
                emergencies, or other circumstances outside the reasonable
                control of ProCraft Solutions.
              </p>
              <p>
                We will make reasonable efforts to communicate significant
                scheduling changes with customers.
              </p>
            </section>

            <section>
              <h2 className="font-display font-bold text-2xl mb-3 text-[#1c1917]">
                SMS / Text Messaging Terms
              </h2>
              <p className="mb-3">
                By providing your mobile phone number and consenting to receive
                text messages from ProCraft Solutions, you agree that ProCraft
                Solutions may send you text messages related to your inquiry or
                services.
              </p>
              <p className="mb-3">Messages may include:</p>
              <ul className="list-disc pl-5 space-y-1.5 text-[#292524] mb-3">
                <li>Estimate and quote communications</li>
                <li>Appointment confirmations and reminders</li>
                <li>Scheduling information</li>
                <li>Service and job updates</li>
                <li>Invoice and payment notifications</li>
                <li>Customer-service communications</li>
                <li>Follow-up communications concerning services</li>
              </ul>
              <p className="mb-3">
                Message frequency varies. Message and data rates may apply.
              </p>
              <p className="mb-3">
                Consent to receive text messages is not a condition of
                purchasing services.
              </p>
              <p className="mb-3">
                To stop receiving text messages, reply <strong>STOP</strong> at
                any time.
              </p>
              <p className="mb-3">
                For assistance, reply <strong>HELP</strong> or contact ProCraft
                Solutions through our website.
              </p>
              <p>
                Wireless carriers are not responsible for delayed or undelivered
                messages.
              </p>
            </section>

            <section>
              <h2 className="font-display font-bold text-2xl mb-3 text-[#1c1917]">
                Privacy
              </h2>
              <p className="mb-3">
                Your privacy is important to us. Information collected through
                our services and text messaging program is handled according to
                our Privacy Policy.
              </p>
              <Link
                to="/privacy-policy"
                className="text-[#16a34a] hover:underline text-sm font-semibold"
                data-testid="terms-privacy-link"
              >
                View our Privacy Policy →
              </Link>
            </section>

            <section>
              <h2 className="font-display font-bold text-2xl mb-3 text-[#1c1917]">
                Customer Responsibilities
              </h2>
              <p>
                Customers are responsible for providing accurate contact and
                property information and informing ProCraft Solutions of known
                conditions that could affect the work, including underground
                utilities, irrigation systems, property boundaries, access
                restrictions, or other relevant conditions.
              </p>
            </section>

            <section>
              <h2 className="font-display font-bold text-2xl mb-3 text-[#1c1917]">
                Changes to These Terms
              </h2>
              <p>
                ProCraft Solutions may update these Terms &amp; Conditions
                periodically. Any updated terms will be posted on this page with
                a revised effective date.
              </p>
            </section>

            <section>
              <h2 className="font-display font-bold text-2xl mb-3 text-[#1c1917]">
                Contact Information
              </h2>
              <p className="mb-3">
                Questions regarding these Terms &amp; Conditions may be directed
                to:
              </p>
              <div className="rounded-2xl border border-[#e7e5df] bg-white p-5">
                <div className="font-display font-bold text-lg text-[#1c1917]">
                  ProCraft Solutions
                </div>
                <a
                  href="https://www.myprocraft.com"
                  className="text-[#16a34a] hover:underline text-sm font-semibold"
                >
                  www.MyProCraft.com
                </a>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}
